/**
 * Player input → Actor. Keyboard direction is read from InputMap (arrows + WASD, 8-dir normalised);
 * touch-pad direction arrives on the bus as input:dir {source:'touch'}; taps arrive as input:tapWorld
 * and become a BFS walk. Emits player:moved (throttled) and honours setInputLocked / setControllable.
 */
import type Phaser from 'phaser';
import { bus, Events, type EventPayloads } from '@/core/events';
import { ActorSystem, StepOrder, type Steppable } from './ActorSystem';
import type { Actor } from './Actor';
import { installInputMap, type InputMapHandle } from './InputMap';
import { SceneEvents } from './sceneEvents';

export interface PlayerControllerOptions {
  /** Map id reported in player:moved. */
  mapId?: string | (() => string);
  /** Override the actor's walk speed (px/s). */
  speed?: number;
  /** Minimum interval between player:moved emissions while moving (default 150 ms). */
  moveEmitMs?: number;
}

export class PlayerController implements Steppable {
  private locked = false;
  private touchDir = { x: 0, y: 0 };
  private sinceEmit = 0;
  private lastEmitted: { x: number; y: number; facing: string };
  private prevPos: { x: number; y: number; facing: string };
  private wasMoving = false;
  private destroyed = false;
  private readonly input: InputMapHandle;
  private readonly system: ActorSystem;
  private readonly onDir = (p: EventPayloads[typeof Events.INPUT_DIR]) => { if (p.source === 'touch') this.touchDir = { x: p.x, y: p.y }; };
  private readonly onTap = (p: EventPayloads[typeof Events.INPUT_TAP_WORLD]) => {
    if (this.locked || !this.actor.isControllable()) return;
    void this.actor.walkTo(p.worldX, p.worldY, this.opts.speed);
  };
  private readonly onShutdown = () => this.destroy();

  constructor(readonly scene: Phaser.Scene, readonly actor: Actor, private readonly opts: PlayerControllerOptions = {}) {
    this.input = installInputMap(scene);
    this.lastEmitted = { x: actor.x, y: actor.y, facing: actor.facing };
    this.prevPos = { ...this.lastEmitted };
    actor.setControllable(true);
    bus.onTyped(Events.INPUT_DIR, this.onDir);
    bus.onTyped(Events.INPUT_TAP_WORLD, this.onTap);
    this.system = ActorSystem.get(scene);
    this.system.add(this, StepOrder.CONTROLLER);
    scene.events.once(SceneEvents.SHUTDOWN, this.onShutdown);
  }

  setInputLocked(locked: boolean): void {
    if (this.locked === locked) return;
    this.locked = locked;
    if (locked) { this.actor.stop(); this.touchDir = { x: 0, y: 0 }; }
  }
  isInputLocked(): boolean { return this.locked; }

  step(dtMs: number): void {
    if (this.destroyed) return;
    const a = this.actor;
    if (!this.locked && a.isControllable()) {
      const k = this.input.dir();
      const dx = k.x !== 0 || k.y !== 0 ? k.x : this.touchDir.x;
      const dy = k.x !== 0 || k.y !== 0 ? k.y : this.touchDir.y;
      if (dx !== 0 || dy !== 0) {
        const len = Math.hypot(dx, dy);
        const speed = this.opts.speed ?? a.speed;
        a.setVelocity((dx / len) * speed, (dy / len) * speed);
      }
    }
    // player:moved — the actor steps after us, so this observes the position produced by the last tick.
    const moved = a.x !== this.prevPos.x || a.y !== this.prevPos.y || a.facing !== this.prevPos.facing;
    this.prevPos = { x: a.x, y: a.y, facing: a.facing };
    this.sinceEmit += dtMs;
    const interval = this.opts.moveEmitMs ?? 150;
    if (moved) { if (this.sinceEmit >= interval) this.emitMoved(); }
    else if (this.wasMoving) this.emitMoved(); // came to rest: always report the final position
    this.wasMoving = moved;
  }

  private emitMoved(): void {
    const a = this.actor;
    this.sinceEmit = 0;
    if (a.x === this.lastEmitted.x && a.y === this.lastEmitted.y && a.facing === this.lastEmitted.facing) return;
    this.lastEmitted = { x: a.x, y: a.y, facing: a.facing };
    const m = this.opts.mapId;
    bus.emitTyped(Events.PLAYER_MOVED, { map: typeof m === 'function' ? m() : m ?? '', x: a.x, y: a.y, facing: a.facing });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    bus.offTyped(Events.INPUT_DIR, this.onDir);
    bus.offTyped(Events.INPUT_TAP_WORLD, this.onTap);
    this.system.remove(this);
    this.scene.events.off(SceneEvents.SHUTDOWN, this.onShutdown);
  }
}
